import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../../config/app-settings'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Loader from '../../pages/loader'
import {
  useGetNewM3uLinksQuery,
  useMarkNewM3uLinksEpisodeMutation
} from '../../../redux/apis/linkManagement/m3u/lmNewM3uApiSlice'
import { Card, CardBody, CardFooter } from '../../../components/other/card/card'
import ZoomableImageViewer from '../../../components/reviewing/ZoomableImageViewer'
import CustomerChannelsDropdown from '../../../components/CustomerChannelsDropdown'

const LiveStreamingCampaignByCustomer = () => {
  const context = useContext(AppSettings)
  const { id } = useParams()
  const [current, setCurrent] = useState(0)
  const [markedLinks, setMarkedLinks] = useState([])
  const [showChannelDropdown, setShowChannelDropdown] = useState(false)
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [toRecordVideo, setToRecordVideo] = useState(false)

  const { data, isLoading, error } = useGetNewM3uLinksQuery({
    expand: ['customer', 'domain'].join(','),
    customer: id,
    reviewed: false,
    status:"active",
    page_size: 200
  })

  const [mark, { isLoading: markLoading }] = useMarkNewM3uLinksEpisodeMutation()

  const results = data?.data?.results || []
  const currentRecord = results[current]
  const count = data?.data?.count

  useEffect(() => {
      if (data?.data?.count === 0) {
        toast.info('All links processed. Closing tab...')
        const timeout = setTimeout(() => {
          window.close()
        }, 1500)
  
        return () => clearTimeout(timeout)
      }
    }, [data?.data?.count])

  const handleMark = (status, record) => {
    if (!record) return

    if (status === 'related' && (record.mode === 'channel' || record.mode === 'vod')) {
      setShowChannelDropdown(true)
      // Y alone stores nothing here, so say so — otherwise it reads as a dead key
      toast.info(
        record.mode === 'channel'
          ? 'Pick a channel below to mark this as related'
          : 'Confirm below to mark this VOD as related'
      )
      return
    }

    // Hide dropdown and reset states when marking as unrelated or review_again
    setShowChannelDropdown(false)
    setSelectedChannel(null)
    setToRecordVideo(false)

    // functional update: two keypresses in the same commit can't lose a mark
    setMarkedLinks(prev =>
      prev.some(item => item.id === record.id)
        ? prev.map(item => (item.id === record.id ? { ...item, status } : item))
        : [...prev, { id: record.id, status }]
    )

    // Automatically move to next item after marking as unrelated or review_again
    if (current < results.length - 1) {
      switchTo(current + 1)
    } else {
      toast.info('Last link on this page — press Submit to load more')
    }
  }

  const handleSelectChannel = channelId => {
    setSelectedChannel(channelId)

    if (!currentRecord) return

    const updated = [
      ...markedLinks.filter(item => item.id !== currentRecord.id),
      {
        id: currentRecord.id,
        status: 'related',
        customer_channel: channelId,
        to_record_video: toRecordVideo
      }
    ]
    setMarkedLinks(updated)
    toast.success('Channel saved for related link')

    // Automatically move to next item after saving
    if (current < results.length - 1) {
      switchTo(current + 1)
    }
  }

  const handleCheckboxChange = e => {
    const checked = e.target.checked
    setToRecordVideo(checked)

    if (!currentRecord) return

    const mode = currentRecord.mode

    if (mode === 'channel' && !selectedChannel) {
      toast.error('Please select a channel first')
      return
    }

    const updated = [
      ...markedLinks.filter(item => item.id !== currentRecord.id),
      {
        id: currentRecord.id,
        status: 'related',
        ...(mode === 'channel' && {
          customer_channel: selectedChannel,
          to_record_video: checked
        }),
        ...(mode === 'vod' && {
          to_record_video: checked
        })
      }
    ]
    setMarkedLinks(updated)
    toast.success(mode === 'channel' ? 'Record setting saved' : 'VOD record setting saved')

    // Automatically move to next item after saving
    if (current < results.length - 1) {
      switchTo(current + 1)
    }
  }


  const handleSubmit = async () => {
    try {
      const response = await mark(markedLinks).unwrap()
      if (response?.success) {
        toast.success(response.message || 'All marked links submitted successfully!')
        setMarkedLinks([])
        setCurrent(0)
      }
      if (response?.data?.errors && response.data.errors.length > 0) {
        response.data.errors.forEach(err =>
          toast.error(`${err.url || err.id}: ${err.error}`)
        )
      }
    } catch (err) {
      toast.error('Failed to submit. Please try again.')
    }
  }

  // ponytail: instant. The old 300ms spinner delay left currentRecord stale, so a
  // second keypress inside the window re-marked the previous link and skipped the next.
  const switchTo = setCurrent

  const handlePrevious = () => {
    if (current > 0) switchTo(current - 1)
  }

  const getButtonClass = type =>
    markedLinks.find(
      link => link.id === currentRecord?.id && link.status === type
    )
      ? 'active-status btn'
      : type === 'related'
      ? 'btn btn-theme'
      : type === 'unrelated'
      ? 'btn btn-default'
      : type === 'reviewed_removed'
      ? 'btn btn-danger'
      : 'btn btn-info'

  useEffect(() => {
    if (error) toast.error('Failed to fetch links')
  }, [error])

  useEffect(() => {
    setShowChannelDropdown(false)
    setSelectedChannel(null)
    setToRecordVideo(false)
  }, [currentRecord?.id])

  useEffect(() => {
    const handleKeyDown = e => {
      if (!currentRecord) return
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return
      const tag = e.target?.tagName
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || e.target?.isContentEditable) return
      const key = e.key.toLowerCase()
      if (key === 'y') handleMark('related', currentRecord)
      if (key === 'n') handleMark('unrelated', currentRecord)
      if (key === 'v') handleMark('review_again', currentRecord)
      if (key === 'r') handleMark('reviewed_removed', currentRecord)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentRecord, markedLinks])

  useEffect(() => {
    if (data?.data?.count === 0) {
      toast.info('All links processed. Closing tab...')
      const timeout = setTimeout(() => {
        window.close()
      }, 1500)
      return () => clearTimeout(timeout)
    }
  }, [data?.data?.count])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)
    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
    // ponytail: [] not [context] — AppSettings.Provider makes a new value object every
    // render, so [context] re-ran this effect forever (header true/false render loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='w-full flex flex-col gap-4'>
      <ul className='breadcrumb'>
        <li className='breadcrumb-item'>
          <p className='text-[#249d79] font-extrabold'>Live Streaming</p>
        </li>
        <li className='breadcrumb-item active'>
          {currentRecord?.customer?.customer_name?.toUpperCase()}
        </li>
      </ul>

      <Card>
        <CardBody className='h-[70vh] overflow-hidden'>
          <div className='flex flex-col md:flex-row gap-4 h-full'>
            <div className='flex flex-col gap-2 w-full md:w-1/2 overflow-auto p-2'>
              <div className='flex gap-1'>
                <p className='font-bold'>
                  {currentRecord?.campaign != null ? 'LIVE CHANNEL' : ''}
                </p>
              </div>
              <div className='flex gap-1'>
                <p className='font-bold'>Current Link:</p>
                <a
                  href={currentRecord?.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='underline break-all'
                >
                  {currentRecord?.url}
                </a>
              </div>
              <div className='flex gap-1'>
                <p className='font-bold'>Domain Name:</p>
                <p>{currentRecord?.domain?.domain_name?.toUpperCase()}</p>
              </div>
              <div className='flex gap-1'>
                <p className='font-bold'>Keyword:</p>
                <p>{currentRecord?.keyword?.toUpperCase()}</p>
              </div>
              {currentRecord?.mode === 'vod' && currentRecord?.campaign && (
                <div className='flex gap-1'>
                  <p className='font-bold'>Campaign:</p>
                  <p>{currentRecord.campaign.campaign_name}</p>
                </div>
              )}

              {showChannelDropdown && (
                <div className='mt-2 border p-3 rounded'>
                  {currentRecord?.mode === 'channel' && (
                    <>
                      <CustomerChannelsDropdown
                        customer={currentRecord?.customer?.id}
                        value={selectedChannel}
                        onChange={handleSelectChannel}
                      />
                      <div className='mt-2 flex items-center gap-2'>
                        <input
                          type='checkbox'
                          id='toRecordVideo'
                          checked={toRecordVideo}
                          onChange={handleCheckboxChange}
                        />
                        <label htmlFor='toRecordVideo'>Set to Record Video</label>
                      </div>
                    </>
                  )}
                  {currentRecord?.mode === 'vod' && (
                    <div className='flex flex-col gap-2'>
                      <div className='mt-2 flex items-center gap-2'>
                        <input
                          type='checkbox'
                          id='toRecordVideoVod'
                          checked={toRecordVideo}
                          onChange={e => setToRecordVideo(e.target.checked)}
                        />
                        <label htmlFor='toRecordVideoVod'>Set to Record Video</label>
                      </div>
                      <button
                        className='btn btn-theme btn-sm mt-1'
                        onClick={() => {
                          if (!currentRecord) return
                          const updated = [
                            ...markedLinks.filter(item => item.id !== currentRecord.id),
                            { id: currentRecord.id, status: 'related', to_record_video: toRecordVideo }
                          ]
                          setMarkedLinks(updated)
                          setShowChannelDropdown(false)
                          toast.success('VOD marked as related')
                          if (current < results.length - 1) switchTo(current + 1)
                        }}
                      >
                        Confirm &amp; Next
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className='flex gap-2 flex-wrap mt-auto'>
                <button
                  className={getButtonClass('related')}
                  onClick={() => handleMark('related', currentRecord)}
                >
                  Related (Y)
                </button>
                <button
                  className={getButtonClass('unrelated')}
                  onClick={() => handleMark('unrelated', currentRecord)}
                >
                  Unrelated (N)
                </button>
                <button
                  className={getButtonClass('review_again')}
                  onClick={() => handleMark('review_again', currentRecord)}
                >
                  Review Again (V)
                </button>
                <button
                  className={getButtonClass('reviewed_removed')}
                  onClick={() => handleMark('reviewed_removed', currentRecord)}
                >
                  Mark Removed (R)
                </button>
              </div>
            </div>

            <div className='w-full md:w-1/2 h-full overflow-y-auto p-2 border-l'>
              <ZoomableImageViewer
                src={currentRecord?.screenshot_url}
                containerClass='w-full h-full p-2 border-l'
              />
            </div>
          </div>
        </CardBody>

        <CardFooter>
          <div className='flex gap-2 justify-between flex-wrap'>
            <div className='flex gap-2 items-center'>
              <button
                className='btn btn-theme'
                disabled={current === 0}
                onClick={handlePrevious}
              >
                Previous
              </button>
              <button
                className='btn btn-default'
                disabled={current === results.length - 1}
                onClick={() => switchTo(current + 1)}
              >
                Next
              </button>
              <p className='my-auto'>
                {current + 1}/{results.length} out of {count}
              </p>
            </div>

            {markedLinks.length > 0 && (
              <button className='btn btn-success' onClick={handleSubmit}>
                Submit ({markedLinks.length}/{count})
              </button>
            )}
          </div>
        </CardFooter>
      </Card>

      <Loader isLoading={isLoading || markLoading} />
    </div>
  )
}

export default LiveStreamingCampaignByCustomer
